/**
 * @fileoverview Modulo wrapper per l'integrazione con Swym Wishlist API
 *
 * Questo modulo gestisce tutte le operazioni relative alla wishlist dell'utente,
 * interfacciandosi con il servizio esterno Swym (https://swym.it/).
 *
 * Funzionalità principali:
 * - Verifica/richiesta del consenso "preferenze" richiesto da Swym (modale di consenso)
 * - Caricamento/creazione wishlist utente
 * - Aggiunta/rimozione prodotti dalla wishlist
 * - Verifica stato prodotto in wishlist
 * - Sistema di queue per gestire il caricamento asincrono di Swym
 *
 * @module wishlist
 * @requires window._swat - Swym SDK (caricato esternamente)
 * @requires window.Shopify - Shopify SDK
 *
 * @example
 * // Import delle funzioni
 * import { addToWishlist, removeFromWishlist, isInWishlist } from './wishlist';
 *
 * // Aggiungere un prodotto
 * addToWishlist(
 *   { epi: '123456', empi: '789', du: 'https://shop.com/product' },
 *   (data) => console.log('Aggiunto!', data),
 *   (err) => console.error('Errore', err)
 * );
 *
 * @fires theme:wishlist:loaded - Quando la wishlist è stata caricata/creata
 * @fires theme:wishlist:added - Quando un prodotto viene aggiunto
 * @fires theme:wishlist:removed - Quando un prodotto viene rimosso
 */
// ============================================
// CONSENSO WISHLIST (Cookiebot ↔ Shopify ↔ Swym)
// ============================================
//
// L'app embed di Swym carica l'SDK (`window._swat`) solo se
// `Shopify.customerPrivacy.preferencesProcessingAllowed()` è true (o se nella regione
// del visitatore non serve consenso). Il consenso Shopify è allineato a Cookiebot da
// `snippets/cookie-consent.liquid`.
//
// Se l'utente non ha concesso la categoria "preferenze", al click su "aggiungi alla
// wishlist" mostriamo la modale `snippets/wishlist-consent-modal.liquid`. Su "Accetta"
// concediamo SOLO le preferenze (Cookiebot + Shopify, le altre categorie restano
// invariate), Swym si carica su `visitorConsentCollected` e l'azione viene completata.
/** Timeout attesa caricamento Swym dopo la concessione del consenso */
const WISHLIST_LOAD_TIMEOUT = 10000;
/** Richiesta di consenso in corso (evita modali multiple su click ripetuti) */
let pendingConsentRequest = null;
/**
 * Verifica se il visitatore ha il consenso necessario a Swym (categoria "preferenze").
 *
 * @function hasWishlistConsent
 * @returns {boolean} `true` se la wishlist può essere usata senza chiedere il consenso
 */
export function hasWishlistConsent() {
    var _a, _b, _c, _d;
    if ((_a = window.Shopify) === null || _a === void 0 ? void 0 : _a.designMode)
        return true;
    const allowed = (_d = (_c = (_b = window.Shopify) === null || _b === void 0 ? void 0 : _b.customerPrivacy) === null || _c === void 0 ? void 0 : _c.preferencesProcessingAllowed) === null || _d === void 0 ? void 0 : _d.call(_c);
    if (typeof allowed === 'boolean')
        return allowed;
    // Customer Privacy API non disponibile: decide la presenza dell'SDK Swym
    return typeof window._swat !== 'undefined';
}
/**
 * Esegue la callback se il consenso "preferenze" manca, attendendo prima che la
 * Customer Privacy API sia disponibile (prima non è possibile saperlo).
 * Usata dai bottoni wishlist per togliere lo spinner iniziale: senza consenso Swym
 * non si carica e `theme:wishlist:loaded` non arriverebbe mai.
 *
 * @function onWishlistConsentMissing
 * @param {() => void} callback - Eseguita solo se il consenso manca
 * @returns {void}
 */
export function onWishlistConsentMissing(callback) {
    onWishlistConsentKnown(hasConsent => {
        if (!hasConsent)
            callback();
    });
}
/**
 * Esegue la callback con lo stato del consenso "preferenze", appena la
 * Customer Privacy API è disponibile.
 *
 * @function onWishlistConsentKnown
 * @param {(hasConsent: boolean) => void} callback
 * @returns {void}
 */
export function onWishlistConsentKnown(callback) {
    var _a, _b;
    const check = () => callback(hasWishlistConsent());
    if (((_a = window.Shopify) === null || _a === void 0 ? void 0 : _a.customerPrivacy) || typeof ((_b = window.Shopify) === null || _b === void 0 ? void 0 : _b.loadFeatures) !== 'function') {
        check();
        return;
    }
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], check);
}
/**
 * Mostra la modale di consenso wishlist e, se l'utente accetta, concede il consenso
 * e attende che la wishlist sia caricata.
 *
 * @function requestWishlistConsent
 * @returns {Promise<boolean>} `true` se il consenso è stato concesso e la wishlist è pronta,
 *   `false` se l'utente rifiuta/chiude la modale o il caricamento fallisce
 * @fires theme:wishlist:consent-open - Apre la modale (ascoltato da wishlist-consent-modal.liquid)
 */
export function requestWishlistConsent() {
    if (pendingConsentRequest)
        return pendingConsentRequest;
    pendingConsentRequest = new Promise(resolve => {
        const onAccept = () => {
            var _a, _b;
            cleanup();
            (_b = (_a = window.LOG) === null || _a === void 0 ? void 0 : _a.info) === null || _b === void 0 ? void 0 : _b.call(_a, '[Wishlist] Consenso accettato dalla modale');
            grantWishlistConsent()
                .then(granted => granted ? waitForWishlist() : false)
                .then(resolve);
        };
        const onDecline = () => {
            var _a, _b;
            cleanup();
            (_b = (_a = window.LOG) === null || _a === void 0 ? void 0 : _a.info) === null || _b === void 0 ? void 0 : _b.call(_a, '[Wishlist] Consenso rifiutato dalla modale, azione annullata');
            resolve(false);
        };
        const cleanup = () => {
            window.removeEventListener('theme:wishlist:consent-accept', onAccept);
            window.removeEventListener('theme:wishlist:consent-decline', onDecline);
        };
        window.addEventListener('theme:wishlist:consent-accept', onAccept);
        window.addEventListener('theme:wishlist:consent-decline', onDecline);
        window.dispatchEvent(new CustomEvent('theme:wishlist:consent-open'));
    }).finally(() => {
        pendingConsentRequest = null;
    });
    return pendingConsentRequest;
}
/**
 * Concede la categoria "preferenze" su Cookiebot e su Shopify, lasciando invariate
 * le altre categorie già scelte dall'utente.
 *
 * @function grantWishlistConsent
 * @returns {Promise<boolean>} `true` se il consenso Shopify è stato salvato
 */
function grantWishlistConsent() {
    var _a, _b, _c, _d;
    // Cookiebot: preferenze on, statistiche e marketing restano come scelti.
    // Scatena CookiebotOnConsentReady → cookie-consent.liquid riallinea Shopify.
    const cookiebot = window.Cookiebot;
    if (typeof (cookiebot === null || cookiebot === void 0 ? void 0 : cookiebot.submitCustomConsent) === 'function') {
        try {
            cookiebot.submitCustomConsent(true, ((_a = cookiebot.consent) === null || _a === void 0 ? void 0 : _a.statistics) === true, ((_b = cookiebot.consent) === null || _b === void 0 ? void 0 : _b.marketing) === true);
        }
        catch (error) {
            (_d = (_c = window.LOG) === null || _c === void 0 ? void 0 : _c.error) === null || _d === void 0 ? void 0 : _d.call(_c, '[Wishlist] Errore aggiornamento consenso Cookiebot:', error);
        }
    }
    return new Promise(resolve => {
        var _a, _b, _c;
        const privacy = (_a = window.Shopify) === null || _a === void 0 ? void 0 : _a.customerPrivacy;
        if (typeof (privacy === null || privacy === void 0 ? void 0 : privacy.setTrackingConsent) !== 'function') {
            (_c = (_b = window.LOG) === null || _b === void 0 ? void 0 : _b.error) === null || _c === void 0 ? void 0 : _c.call(_b, '[Wishlist] Shopify Customer Privacy API non disponibile');
            resolve(false);
            return;
        }
        // setTrackingConsent è un aggiornamento parziale: le chiavi omesse restano invariate
        privacy.setTrackingConsent({ preferences: true }, result => {
            var _a, _b, _c, _d;
            if (result === null || result === void 0 ? void 0 : result.error) {
                (_b = (_a = window.LOG) === null || _a === void 0 ? void 0 : _a.error) === null || _b === void 0 ? void 0 : _b.call(_a, '[Wishlist] Errore salvataggio consenso Shopify:', result.error);
                resolve(false);
                return;
            }
            (_d = (_c = window.LOG) === null || _c === void 0 ? void 0 : _c.success) === null || _d === void 0 ? void 0 : _d.call(_c, '[Wishlist] Consenso preferenze concesso');
            // Come fa il consent manager di Swym: notifica l'app embed, che carica l'SDK
            document.dispatchEvent(new CustomEvent('visitorConsentCollected'));
            resolve(true);
        });
    });
}
/**
 * Attende che la wishlist Swym sia caricata (`theme:wishlist:loaded`).
 *
 * @function waitForWishlist
 * @returns {Promise<boolean>} `true` se la wishlist è pronta entro il timeout
 */
function waitForWishlist() {
    var _a;
    if ((_a = window.themeWishlist) === null || _a === void 0 ? void 0 : _a.lid)
        return Promise.resolve(true);
    return new Promise(resolve => {
        const onLoaded = () => {
            clearTimeout(timer);
            resolve(true);
        };
        const timer = setTimeout(() => {
            var _a, _b;
            window.removeEventListener('theme:wishlist:loaded', onLoaded);
            (_b = (_a = window.LOG) === null || _a === void 0 ? void 0 : _a.error) === null || _b === void 0 ? void 0 : _b.call(_a, '[Wishlist] Wishlist non caricata entro il timeout dopo il consenso');
            resolve(false);
        }, WISHLIST_LOAD_TIMEOUT);
        window.addEventListener('theme:wishlist:loaded', onLoaded, { once: true });
    });
}
/**
 * Inizializza il caricamento della wishlist.
 *
 * Gestisce il caso in cui Swym SDK non sia ancora disponibile,
 * accodando la richiesta tramite `window.SwymCallbacks`.
 * Quando Swym sarà pronto, eseguirà automaticamente le callback in coda.
 *
 * @function loadWishlist
 * @returns {void}
 */
export function loadWishlist() {
    var _a, _b, _c, _d;
    if (typeof window._swat === 'undefined') {
        if (!window.SwymCallbacks) {
            window.SwymCallbacks = [];
        }
        window.SwymCallbacks.push(checkWishlist);
        (_b = (_a = window.LOG) === null || _a === void 0 ? void 0 : _a.info) === null || _b === void 0 ? void 0 : _b.call(_a, '[Wishlist] Swym non ancora caricato, checkWishlist messo in coda (queue length:', window.SwymCallbacks.length, ')');
    }
    else {
        (_d = (_c = window.LOG) === null || _c === void 0 ? void 0 : _c.info) === null || _d === void 0 ? void 0 : _d.call(_c, '[Wishlist] Swym già disponibile, esecuzione immediata');
        checkWishlist();
    }
}
/**
 * Verifica l'esistenza della wishlist utente e la carica.
 *
 * Se non esiste nessuna wishlist, ne crea una nuova automaticamente.
 * La wishlist caricata viene salvata in `window.themeWishlist` per
 * accesso globale e viene dispatchato l'evento `theme:wishlist:loaded`.
 *
 * @function checkWishlist
 * @returns {void}
 * @fires theme:wishlist:loaded - Quando la wishlist è pronta
 */
export function checkWishlist() {
    var _a, _b, _c, _d, _e, _f, _g;
    try {
        // Verifica che Swym SDK sia disponibile e funzionante
        if (typeof ((_a = window._swat) === null || _a === void 0 ? void 0 : _a.fetchLists) !== 'function') {
            (_c = (_b = window.LOG) === null || _b === void 0 ? void 0 : _b.warn) === null || _c === void 0 ? void 0 : _c.call(_b, '[Wishlist] Swym SDK non disponibile, wishlist disabilitata');
            return;
        }
        (_e = (_d = window.LOG) === null || _d === void 0 ? void 0 : _d.debug) === null || _e === void 0 ? void 0 : _e.call(_d, '[Wishlist] → fetchLists chiamato');
        window._swat.fetchLists({
            callbackFn: (lists) => {
                var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
                (_b = (_a = window.LOG) === null || _a === void 0 ? void 0 : _a.debug) === null || _b === void 0 ? void 0 : _b.call(_a, '[Wishlist] ← fetchLists risposta raw:', JSON.stringify(lists));
                (_d = (_c = window.LOG) === null || _c === void 0 ? void 0 : _c.debug) === null || _d === void 0 ? void 0 : _d.call(_c, '[Wishlist]   liste trovate:', (_e = lists === null || lists === void 0 ? void 0 : lists.length) !== null && _e !== void 0 ? _e : 0);
                // Se non ci sono liste, creane una nuova
                if (!lists || lists.length === 0) {
                    (_g = (_f = window.LOG) === null || _f === void 0 ? void 0 : _f.info) === null || _g === void 0 ? void 0 : _g.call(_f, '[Wishlist]   nessuna lista trovata → createList');
                    createList();
                    return;
                }
                (_j = (_h = window.LOG) === null || _h === void 0 ? void 0 : _h.debug) === null || _j === void 0 ? void 0 : _j.call(_h, '[Wishlist]   lista selezionata [0]: lid=', lists[0].lid, 'lname=', lists[0].lname, 'items=', (_l = (_k = lists[0].listcontents) === null || _k === void 0 ? void 0 : _k.length) !== null && _l !== void 0 ? _l : 0);
                // Salva la prima lista come wishlist globale
                window.themeWishlist = lists[0];
                (_o = (_m = window.LOG) === null || _m === void 0 ? void 0 : _m.success) === null || _o === void 0 ? void 0 : _o.call(_m, '[Wishlist] Wishlist caricata:', window.themeWishlist);
                // Notifica che la wishlist è pronta
                window.dispatchEvent(new CustomEvent('theme:wishlist:loaded'));
            },
            errorFn: (err) => { var _a, _b; return (_b = (_a = window.LOG) === null || _a === void 0 ? void 0 : _a.error) === null || _b === void 0 ? void 0 : _b.call(_a, '[Wishlist] ← fetchLists errore:', err); }
        });
    }
    catch (error) {
        (_g = (_f = window.LOG) === null || _f === void 0 ? void 0 : _f.error) === null || _g === void 0 ? void 0 : _g.call(_f, '[Wishlist] Errore critico in checkWishlist:', error);
    }
}
/**
 * Crea una nuova wishlist per l'utente.
 *
 * Viene chiamata automaticamente da `checkWishlist()` se non esistono liste.
 * Dopo la creazione usa direttamente la risposta dell'API per popolare `window.themeWishlist`,
 * evitando un secondo `fetchLists` che fallirebbe per i guest prima che il `di` (device identifier)
 * sia memorizzato da Swym.
 *
 * @function createList
 * @returns {void}
 */
export function createList() {
    var _a, _b, _c, _d, _e, _f, _g;
    try {
        // Verifica che Swym SDK sia disponibile
        if (typeof ((_a = window._swat) === null || _a === void 0 ? void 0 : _a.createList) !== 'function') {
            (_c = (_b = window.LOG) === null || _b === void 0 ? void 0 : _b.warn) === null || _c === void 0 ? void 0 : _c.call(_b, '[Wishlist] Swym SDK non disponibile per createList');
            return;
        }
        (_e = (_d = window.LOG) === null || _d === void 0 ? void 0 : _d.debug) === null || _e === void 0 ? void 0 : _e.call(_d, '[Wishlist] → createList chiamato (lname: "Wishlist")');
        window._swat.createList({
            lname: 'Wishlist'
        }, (newList) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
            (_b = (_a = window.LOG) === null || _a === void 0 ? void 0 : _a.debug) === null || _b === void 0 ? void 0 : _b.call(_a, '[Wishlist] ← createList risposta raw:', JSON.stringify(newList));
            (_d = (_c = window.LOG) === null || _c === void 0 ? void 0 : _c.debug) === null || _d === void 0 ? void 0 : _d.call(_c, '[Wishlist]   lid:', newList === null || newList === void 0 ? void 0 : newList.lid);
            (_f = (_e = window.LOG) === null || _e === void 0 ? void 0 : _e.debug) === null || _f === void 0 ? void 0 : _f.call(_e, '[Wishlist]   di (device identifier):', newList === null || newList === void 0 ? void 0 : newList.di);
            (_h = (_g = window.LOG) === null || _g === void 0 ? void 0 : _g.debug) === null || _h === void 0 ? void 0 : _h.call(_g, '[Wishlist]   userinfo (loggato?):', newList === null || newList === void 0 ? void 0 : newList.userinfo);
            // Usa i dati restituiti direttamente invece di ri-chiamare fetchLists.
            // Per i guest, fetchLists richiederebbe il di (device identifier) già memorizzato
            // da Swym — ri-fetchare subito può causare lista vuota se il di non è ancora pronto.
            window.themeWishlist = newList;
            (_k = (_j = window.LOG) === null || _j === void 0 ? void 0 : _j.success) === null || _k === void 0 ? void 0 : _k.call(_j, '[Wishlist] Wishlist creata e salvata in themeWishlist');
            window.dispatchEvent(new CustomEvent('theme:wishlist:loaded'));
        }, (err) => { var _a, _b; return (_b = (_a = window.LOG) === null || _a === void 0 ? void 0 : _a.error) === null || _b === void 0 ? void 0 : _b.call(_a, '[Wishlist] ← createList errore:', err); });
    }
    catch (error) {
        (_g = (_f = window.LOG) === null || _f === void 0 ? void 0 : _f.error) === null || _g === void 0 ? void 0 : _g.call(_f, '[Wishlist] Errore critico in createList:', error);
    }
}
/**
 * Aggiunge un prodotto alla wishlist.
 *
 * @function addToWishlist
 * @param {SwymItem} product - Prodotto da aggiungere
 * @param {Function} onSuccess - Callback chiamata in caso di successo
 * @param {Function} onError - Callback chiamata in caso di errore
 * @returns {void}
 * @fires theme:wishlist:added - Con il dettaglio del prodotto aggiunto
 *
 * @example
 * addToWishlist(
 *   { epi: '47511834099867', empi: '8757020950683', du: 'https://shop.com/products/my-product' },
 *   (data) => console.log('Prodotto aggiunto:', data),
 *   (err) => console.error('Errore:', err)
 * );
 */
export function addToWishlist(product, onSuccess, onError) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    try {
        // Verifica che Swym SDK e wishlist siano disponibili
        if (typeof ((_a = window._swat) === null || _a === void 0 ? void 0 : _a.addToList) !== 'function') {
            const error = new Error('Swym SDK non disponibile');
            (_c = (_b = window.LOG) === null || _b === void 0 ? void 0 : _b.error) === null || _c === void 0 ? void 0 : _c.call(_b, '[Wishlist] addToWishlist fallito:', error);
            onError(error);
            return;
        }
        if (!((_d = window.themeWishlist) === null || _d === void 0 ? void 0 : _d.lid)) {
            const error = new Error('Wishlist non caricata');
            (_f = (_e = window.LOG) === null || _e === void 0 ? void 0 : _e.error) === null || _f === void 0 ? void 0 : _f.call(_e, '[Wishlist] addToWishlist fallito:', error);
            onError(error);
            return;
        }
        (_h = (_g = window.LOG) === null || _g === void 0 ? void 0 : _g.debug) === null || _h === void 0 ? void 0 : _h.call(_g, '[Wishlist] → addToList chiamato: lid=', window.themeWishlist.lid, 'product=', JSON.stringify(product));
        window._swat.addToList(window.themeWishlist.lid, product, (data) => {
            var _a, _b;
            (_b = (_a = window.LOG) === null || _a === void 0 ? void 0 : _a.debug) === null || _b === void 0 ? void 0 : _b.call(_a, '[Wishlist] ← addToList risposta raw:', JSON.stringify(data));
            window.dispatchEvent(new CustomEvent('theme:wishlist:added', { detail: data }));
            onSuccess(data);
        }, (err) => {
            var _a, _b;
            (_b = (_a = window.LOG) === null || _a === void 0 ? void 0 : _a.error) === null || _b === void 0 ? void 0 : _b.call(_a, '[Wishlist] ← addToList errore:', err);
            onError(err);
        });
    }
    catch (error) {
        (_k = (_j = window.LOG) === null || _j === void 0 ? void 0 : _j.error) === null || _k === void 0 ? void 0 : _k.call(_j, '[Wishlist] Errore critico in addToWishlist:', error);
        onError(error);
    }
}
/**
 * Rimuove un prodotto dalla wishlist.
 *
 * @function removeFromWishlist
 * @param {SwymItem} product - Prodotto da rimuovere
 * @param {Function} onSuccess - Callback chiamata in caso di successo
 * @param {Function} onError - Callback chiamata in caso di errore
 * @returns {void}
 * @fires theme:wishlist:removed - Con il dettaglio del prodotto rimosso
 */
export function removeFromWishlist(product, onSuccess, onError) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    try {
        // Verifica che Swym SDK e wishlist siano disponibili
        if (typeof ((_a = window._swat) === null || _a === void 0 ? void 0 : _a.deleteFromList) !== 'function') {
            const error = new Error('Swym SDK non disponibile');
            (_c = (_b = window.LOG) === null || _b === void 0 ? void 0 : _b.error) === null || _c === void 0 ? void 0 : _c.call(_b, '[Wishlist] removeFromWishlist fallito:', error);
            onError(error);
            return;
        }
        if (!((_d = window.themeWishlist) === null || _d === void 0 ? void 0 : _d.lid)) {
            const error = new Error('Wishlist non caricata');
            (_f = (_e = window.LOG) === null || _e === void 0 ? void 0 : _e.error) === null || _f === void 0 ? void 0 : _f.call(_e, '[Wishlist] removeFromWishlist fallito:', error);
            onError(error);
            return;
        }
        (_h = (_g = window.LOG) === null || _g === void 0 ? void 0 : _g.debug) === null || _h === void 0 ? void 0 : _h.call(_g, '[Wishlist] → deleteFromList chiamato: lid=', window.themeWishlist.lid, 'product=', JSON.stringify(product));
        window._swat.deleteFromList(window.themeWishlist.lid, product, (data) => {
            var _a, _b;
            (_b = (_a = window.LOG) === null || _a === void 0 ? void 0 : _a.debug) === null || _b === void 0 ? void 0 : _b.call(_a, '[Wishlist] ← deleteFromList risposta raw:', JSON.stringify(data));
            window.dispatchEvent(new CustomEvent('theme:wishlist:removed', { detail: data }));
            onSuccess(data);
        }, (err) => {
            var _a, _b;
            (_b = (_a = window.LOG) === null || _a === void 0 ? void 0 : _a.error) === null || _b === void 0 ? void 0 : _b.call(_a, '[Wishlist] ← deleteFromList errore:', err);
            onError(err);
        });
    }
    catch (error) {
        (_k = (_j = window.LOG) === null || _j === void 0 ? void 0 : _j.error) === null || _k === void 0 ? void 0 : _k.call(_j, '[Wishlist] Errore critico in removeFromWishlist:', error);
        onError(error);
    }
}
/**
 * Verifica se un prodotto è presente nella wishlist.
 *
 * @function isInWishlist
 * @param {SwymItem} product - Prodotto da verificare (serve solo `epi`)
 * @returns {boolean} `true` se il prodotto è in wishlist, `false` altrimenti
 */
export function isInWishlist(product) {
    if (!window.themeWishlist || !window.themeWishlist.listcontents)
        return false;
    return window.themeWishlist.listcontents.some(item => item.epi == product.epi);
}
/**
 * Alterna lo stato di un prodotto nella wishlist (add/remove toggle).
 *
 * Se il prodotto è già in wishlist, lo rimuove.
 * Se il prodotto non è in wishlist, lo aggiunge.
 *
 * @function toggleWishlistItem
 * @param {SwymItem} product - Prodotto da aggiungere/rimuovere
 * @param {Function} onSuccess - Callback chiamata in caso di successo
 * @param {Function} onError - Callback chiamata in caso di errore
 * @returns {void}
 */
export function toggleWishlistItem(product, onSuccess, onError) {
    var _a, _b;
    const action = isInWishlist(product) ? 'remove' : 'add';
    (_b = (_a = window.LOG) === null || _a === void 0 ? void 0 : _a.debug) === null || _b === void 0 ? void 0 : _b.call(_a, '[Wishlist] toggleWishlistItem: epi=', product.epi, 'azione=', action);
    if (action === 'remove') {
        removeFromWishlist(product, onSuccess, onError);
    }
    else {
        addToWishlist(product, onSuccess, onError);
    }
}
// ============================================
// INIZIALIZZAZIONE AUTOMATICA
// ============================================
// Queste funzioni vengono eseguite automaticamente
// quando il modulo viene importato
loadWishlist();
